// ============================================================
// CarePlanRolePanel.jsx  (Care Coordinator, role-model expansion Push 2)
// User-detail panel for the CarePlan Care Coordinator overlay. Clone of
// CareChargeRolePanel (single value, no tier). Rendered in the Agent Access
// tab. Self-gates on coreRoleApplicable (carer + admin, §2.3; NA note for
// manager/auditor). Backend is the source of truth — no optimistic UI.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertCircle, RotateCw, ShieldCheck } from 'lucide-react';
import Badge from './Badge';
import ConfirmDialog from './ConfirmDialog';
import CarePlanRoleAssignModal from './CarePlanRoleAssignModal';
import { toast } from './Toaster';
import { getCarePlanRole, grantCarePlanRole, revokeCarePlanRole } from '../api/carePlanRoles';
import { translateError } from '../utils/errorMessages';
import StatusMessage from './StatusMessage';
import { formatDate } from '../utils/format';
import './CarePeopleRolePanel.css';

const ROLE_LABEL = { care_coordinator: 'Care Coordinator' };
function roleLabel(role) { return ROLE_LABEL[role] || role || ''; }

export default function CarePlanRolePanel({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignError, setAssignError] = useState(null);

  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeSubmitting, setRevokeSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCarePlanRole(user.clientUserId);
      setData(res);
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [user.clientUserId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function openAssign() {
    setAssignError(null);
    setAssignOpen(true);
  }

  async function handleAssignSubmit({ carePlanRole }) {
    setAssignSubmitting(true);
    setAssignError(null);
    try {
      await grantCarePlanRole(user.clientUserId, { carePlanRole });
      toast.success('Care Coordinator role assigned');
      setAssignOpen(false);
      await fetchData();
    } catch (err) {
      setAssignError(translateError(err));
    } finally {
      setAssignSubmitting(false);
    }
  }

  async function handleRevokeConfirm() {
    setRevokeSubmitting(true);
    try {
      await revokeCarePlanRole(user.clientUserId);
      toast.success('Care Coordinator role revoked');
      setRevokeOpen(false);
      await fetchData();
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setRevokeSubmitting(false);
    }
  }

  if (loading && !data) {
    return (
      <StatusMessage variant="loading" className="cp-role-panel__state">
        <Loader2 size={16} className="cp-role-panel__spinner" />
        <span>Loading Care Coordinator role…</span>
      </StatusMessage>
    );
  }

  if (error) {
    return (
      <StatusMessage variant="error" className="cp-role-panel__state cp-role-panel__state--error">
        <AlertCircle size={16} />
        <span>{error}</span>
        <button type="button" className="cp-role-panel__retry" onClick={fetchData}>
          <RotateCw size={14} />
          Retry
        </button>
      </StatusMessage>
    );
  }

  if (!data) return null;

  // manager / auditor — the coordinator overlay does not apply (§2.3).
  if (!data.coreRoleApplicable) {
    return (
      <section className="cp-role-panel cp-role-panel--na" data-testid="cpl-role-panel-na">
        <h3 className="cp-role-panel__heading">CarePlan Module Role</h3>
        <div className="cp-role-panel__na-note">
          <ShieldCheck size={16} />
          <p>
            The Care Coordinator overlay applies only to carer and admin users —
            managers already have full CarePlan access and auditors are read-only.
          </p>
        </div>
      </section>
    );
  }

  const current = data.current;
  const history = data.history || [];

  return (
    <section className="cp-role-panel" data-testid="cpl-role-panel">
      <h3 className="cp-role-panel__heading">CarePlan Module Role</h3>

      <div className="cp-role-panel__current">
        <div className="cp-role-panel__current-main">
          <span className="cp-role-panel__current-label">Current role</span>
          {current ? (
            <span className="cp-role-panel__current-value" data-testid="cpl-role-current">
              <Badge variant="teal">{roleLabel(current.carePlanRole)}</Badge>
            </span>
          ) : (
            <span className="cp-role-panel__current-value" data-testid="cpl-role-current">
              <Badge variant="neutral">None</Badge>
            </span>
          )}
        </div>
        {current && (
          <p className="cp-role-panel__current-meta">
            Assigned {formatDate(current.grantedAt)}{current.grantedBy && <> by {current.grantedBy}</>}
          </p>
        )}
        <div className="cp-role-panel__actions">
          {!current && (
            <button type="button" className="cp-role-panel__change" onClick={openAssign}>
              Assign role
            </button>
          )}
          {current && (
            <button
              type="button"
              className="cp-role-panel__revoke"
              onClick={() => setRevokeOpen(true)}
            >
              Revoke access
            </button>
          )}
        </div>
      </div>

      <div className="cp-role-panel__history" data-testid="cpl-role-history">
        <h4 className="cp-role-panel__history-heading">Role history</h4>
        {history.length === 0 ? (
          <p className="cp-role-panel__history-empty">No role changes recorded yet.</p>
        ) : (
          <ul className="cp-role-panel__history-list">
            {history.map((h, i) => {
              const vals = h.action === 'granted' ? h.newValues : h.oldValues;
              const desc = vals ? roleLabel(vals.carePlanRole) : '';
              return (
                <li key={`${h.changedAt}-${i}`} className="cp-role-panel__history-row">
                  <span className="cp-role-panel__history-line">
                    <Badge variant={h.action === 'granted' ? 'success' : 'danger'}>
                      {h.action === 'granted' ? 'Granted' : 'Revoked'}
                    </Badge>
                    <span className="cp-role-panel__history-role">{desc}</span>
                    <span className="cp-role-panel__history-date">{formatDate(h.changedAt)}</span>
                  </span>
                  {h.changedBy && (
                    <span className="cp-role-panel__history-by">by {h.changedBy}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <CarePlanRoleAssignModal
        isOpen={assignOpen}
        onClose={() => { if (!assignSubmitting) setAssignOpen(false); }}
        onSubmit={handleAssignSubmit}
        submitting={assignSubmitting}
        errorMessage={assignError}
      />

      <ConfirmDialog
        isOpen={revokeOpen}
        onClose={() => { if (!revokeSubmitting) setRevokeOpen(false); }}
        onConfirm={handleRevokeConfirm}
        title="Revoke Care Coordinator access?"
        message={
          current
            ? `This removes ${user.email || 'this user'}'s Care Coordinator overlay immediately. You can reassign it later.`
            : ''
        }
        confirmLabel="Confirm revoke"
        isSubmitting={revokeSubmitting}
      />
    </section>
  );
}
